// index.js
import * as Sentry from "@sentry/react";
import './setupConsoleOverrides';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import ReactGA from 'react-ga4';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import App from './App';
import './index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { LanguageProvider } from './Context/LanguageContext';
import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';

// Initialize Sentry only in TEST and PROD environments
const currentEnv = process.env.REACT_APP_ENV;
const isSentryEnabled = currentEnv === 'TEST' || currentEnv === 'PROD';

Sentry.init({
  dsn: "https://d9b5dc1b5f524587cc08ffae16f99149@o4510601398255616.ingest.de.sentry.io/4510601399369808",
  enabled: isSentryEnabled,
  environment: currentEnv,
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration(),
  ],
  // Session Replay - only on errors
  replaysSessionSampleRate: 0,     // Don't record normal sessions
  replaysOnErrorSampleRate: 1.0,   // Record 100% of sessions with errors
});

// Initialize Google Analytics only in test and production environments
if (currentEnv === 'TEST') {
  const measurementId = process.env.REACT_APP_TEST_GA_MEASUREMENT_ID;
  if (measurementId) {
    ReactGA.initialize(measurementId);
  } else {
  }
} else if (currentEnv === 'PROD') {
  const measurementId = process.env.REACT_APP_PROD_GA_MEASUREMENT_ID;
  if (measurementId) {
    ReactGA.initialize(measurementId);
  } else {
  }
} else {
}

// Initialize PostHog only in TEST and PROD environments
const isPostHogEnabled = currentEnv === 'TEST' || currentEnv === 'PROD';
if (isPostHogEnabled && process.env.REACT_APP_POSTHOG_KEY) {
  posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host: process.env.REACT_APP_POSTHOG_HOST,
    defaults: '2025-11-30',
    person_profiles: 'identified_only',
  });
}

// Function to remove the initial loader after React mounts
const removeInitialLoader = () => {
  const loader = document.getElementById('initial-loader');
  if (loader) {
    loader.classList.add('initial-loader-fade-out');
    setTimeout(() => {
      loader.remove();
    }, 300); // Match the fade-out animation duration
  }
};

try {
  const newDiv = document.createElement('div');
  newDiv.id = 'brandvox-react';
  document.body.appendChild(newDiv);

  const root = ReactDOM.createRoot(newDiv);

  const AppContent = (
    <BrowserRouter>
      <MantineProvider>
        <DatesProvider>
          <LanguageProvider>
            <App />
          </LanguageProvider>
        </DatesProvider>
      </MantineProvider>
    </BrowserRouter>
  );

  root.render(
    isPostHogEnabled ? (
      <PostHogProvider client={posthog}>
        {AppContent}
      </PostHogProvider>
    ) : (
      AppContent
    )
  );

  // Remove initial loader after React has rendered
  removeInitialLoader();
} catch (error) {
  // Even on error, remove the loader to show the error page
  removeInitialLoader();
}

// Only track events in test/production environments
if ((currentEnv === 'TEST' || currentEnv === 'PROD') && ReactGA.isInitialized) {
  ReactGA.event({
    category: 'App',
    action: 'Initialized',
    label: `Environment: ${currentEnv}`
  });
}

